import { type TableFilterField } from '../types';
import { getFilterEndOfDay, getFilterStartOfDay } from '@shared/helpers';

/**
 * Универсальная функция для формирования queryFilter для API из значений формы.
 *
 * Используется по умолчанию в TableFilters, если не передан свой getQueryFilter.
 *
 * @template T
 * @param {T} values - Значения формы
 * @param {TableFilterField[]} config - Конфиг полей фильтра
 * @returns {any} queryFilter для API (например, { id: { items: ['123'], operator: 'or' }, ... })
 *
 * @example
 * const filter = getDefaultQueryFilter({ id: '123', isActive: 'true' }, config);
 * // { id: { items: ['123'], operator: 'or' }, isActive: { items: ['true'], operator: 'or' } }
 */

export function getDefaultQueryFilter<T extends Record<string, any>>(
  values: T,
  config: TableFilterField[]
): any {
  const queryFilter: Record<string, any> = {};
  config.forEach(field => {
    const value = values[field.name];
    const apiKey = field.apiKey || field.name;

    if (field.type === 'range') {
      const fromValue = values[field.fromName];
      const toValue = values[field.toName];
      const apiKey = field.apiKey || field.name;

      const hasFrom =
        fromValue !== '' && fromValue !== null && fromValue !== undefined;
      const hasTo = toValue !== '' && toValue !== null && toValue !== undefined;

      if (hasFrom && hasTo) {
        // Оба поля заполнены - используем range
        queryFilter[apiKey] = {
          items: [fromValue, toValue],
          operator: 'range',
        };
      } else if (hasFrom) {
        // Только "от" - используем gte (greater than or equal)
        queryFilter[apiKey] = {
          items: [fromValue],
          operator: 'gte',
        };
      } else if (hasTo) {
        // Только "до" - используем lte (less than or equal)
        queryFilter[apiKey] = {
          items: [toValue],
          operator: 'lte',
        };
      }
    }

    if (field.type === 'date-range' && Array.isArray(value)) {
      const hasValidDates = value.some(date => date !== null);
      if (hasValidDates) {
        queryFilter[apiKey] = {
          items: [
            value[0] ? getFilterStartOfDay(value[0]) : '',
            value[1] ? getFilterEndOfDay(value[1]) : '',
          ],
          operator: 'range',
        };
      }
    } else if (field.type === 'select-company' && value) {
      const companyId = value.id ? value.id.toString() : value.toString();
      queryFilter[apiKey] = {
        items: [companyId],
        operator: 'or',
      };
    } else if (
      field.type === 'select-city' ||
      field.type === 'select-activity-sphere' ||
      field.type === 'multi-select'
    ) {
      if (Array.isArray(value) && value.length > 0) {
        queryFilter[apiKey] = {
          items: value,
          operator: 'or',
        };
      }
    } else if (value) {
      queryFilter[apiKey] = { items: [value], operator: 'or' };
    }
  });
  return queryFilter;
}
